import type { AgentDto, ChannelDto, ChannelPostDto } from '@af/shared';
import { Link } from '@tanstack/react-router';
import { Fragment, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ManageChannelDialog } from '@/components/talk/ManageChannelDialog';
import { AgentOrb } from '@/components/ui/agent-orb';
import { Button, Spinner } from '@/components/ui/button';
import { IconChat, IconSettings, IconTasks, IconX } from '@/components/ui/icons';
import { Textarea } from '@/components/ui/input';
import { ErrorBox } from '@/components/ui/misc';
import { relTime, timeOfDay } from '@/lib/format';
import { useCan } from '@/lib/permissions';
import { qk, useAgents, useApiMutation, useChannelPosts, useChannels, useChannelThread } from '@/lib/queries';
import { isTemporaryChannel, mentionAt, newDay, withContinuation } from '@/lib/talk';
import { cn } from '@/lib/utils';

interface Agents {
  byId: Map<string, AgentDto>;
  bySlug: Map<string, AgentDto>;
  list: AgentDto[];
}

function useAgentMaps(): Agents {
  const agentsQ = useAgents();
  return useMemo(() => {
    const list = (agentsQ.data ?? []).filter((a) => a.status !== 'archived' && a.kind === 'persistent');
    return { list, byId: new Map(list.map((a) => [a.id, a])), bySlug: new Map(list.map((a) => [a.slug, a])) };
  }, [agentsQ.data]);
}

/** @mentions stand out in a post (agent slugs). */
function Body({ text, agents }: { text: string; agents: Agents }) {
  const parts = text.split(/(@[a-z0-9][a-z0-9-]*)/g);
  return (
    <p className="text-sm whitespace-pre-wrap break-words text-fg-2">
      {parts.map((p, i) =>
        p.startsWith('@') && agents.bySlug.has(p.slice(1)) ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: static split of one string
          <span key={i} className="rounded-sm bg-accent/10 px-0.5 font-medium text-accent">
            {p}
          </span>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: static split of one string
          <span key={i}>{p}</span>
        ),
      )}
    </p>
  );
}

const nameOf = (agents: Agents, id: unknown) => (typeof id === 'string' && agents.byId.get(id)?.name) || '';

/** A platform line: channel created, members added, task handed over or reported, conversation paused. */
function SystemLine({ p, agents }: { p: ChannelPostDto; agents: Agents }) {
  const { t } = useTranslation();
  const m = p.meta ?? {};
  const who = nameOf(agents, m.agentId) || p.authorName;
  const ids = Array.isArray(m.agentIds) ? (m.agentIds as string[]) : [];
  const others = ids
    .filter((id) => id !== m.agentId)
    .map((id) => nameOf(agents, id))
    .filter(Boolean)
    .join(', ');
  const text = (() => {
    switch (m.event) {
      case 'channel_created':
        return others ? t('talk.sys.createdWith', { who, others }) : t('talk.sys.created', { who });
      case 'members_added':
        return others ? t('talk.sys.added', { who, others }) : t('talk.sys.joined', { who });
      case 'task_delegated':
        return t('talk.sys.delegated', { who, to: nameOf(agents, m.toAgentId), title: p.body });
      case 'task_reported':
        return t(m.status === 'completed' ? 'talk.sys.reported' : 'talk.sys.reportedFailed', {
          who,
          text: p.body,
        });
      case 'wake_stopped':
        return t(`talk.sys.stopped.${String(m.reason ?? 'other')}`, { defaultValue: p.body });
      case 'reply_failed':
        return t('talk.sys.replyFailed', { who });
      default:
        return p.body;
    }
  })();
  const task = m.event === 'task_delegated' || m.event === 'task_reported';
  const Icon = task ? IconTasks : IconSettings;
  const runId = typeof m.runId === 'string' ? m.runId : p.runId;
  return (
    <li className="flex items-start gap-2 px-4 py-1 text-xs text-muted">
      <span aria-hidden className="mt-0.5 w-8 shrink-0 text-center text-faint">
        <Icon size={14} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="mr-1.5 font-semibold">
          {t(task ? 'companyActivity.tasks' : 'companyActivity.system')}:
        </span>
        {text}
        {runId ? (
          <Link to="/runs/$id" params={{ id: runId }} className="ml-1.5 text-accent hover:underline">
            {t('talk.openRun')}
          </Link>
        ) : null}
        <span className="ml-2 text-faint">{timeOfDay(p.createdAt)}</span>
      </span>
    </li>
  );
}

function Message({
  p,
  continued,
  agents,
  onThread,
  inThread,
}: {
  p: ChannelPostDto;
  continued: boolean;
  agents: Agents;
  onThread?: (id: string) => void;
  inThread?: boolean;
}) {
  const { t } = useTranslation();
  if (p.kind === 'system') return <SystemLine p={p} agents={agents} />;
  const agent = p.authorAgentId ? agents.byId.get(p.authorAgentId) : undefined;
  return (
    <li
      className={cn(
        'group relative flex gap-3 px-4 hover:bg-hover/40',
        continued ? 'py-0.5' : 'pt-2.5 pb-0.5',
      )}
    >
      <span className="w-8 shrink-0">
        {continued ? (
          <span className="invisible block pt-1 text-right text-2xs text-faint group-hover:visible">
            {timeOfDay(p.createdAt)}
          </span>
        ) : agent ? (
          <AgentOrb seed={agent.id} size={32} avatar={agent.avatar} name={agent.name} />
        ) : (
          <span className="flex size-8 items-center justify-center rounded-lg bg-panel-2 text-sm font-medium text-fg-2">
            {p.authorName.slice(0, 1).toUpperCase()}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        {continued ? null : (
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-semibold text-fg">{p.authorName}</span>
            <span className="inline-flex items-center gap-1 text-2xs text-muted">
              <IconChat size={12} />
              {t('companyActivity.messages')}
            </span>
            {agent?.roleTitle ? <span className="truncate text-xs text-muted">{agent.roleTitle}</span> : null}
            {!agent && p.authorUserId ? <span className="text-xs text-muted">{t('talk.person')}</span> : null}
            <span className="shrink-0 text-2xs text-faint" title={p.createdAt}>
              {timeOfDay(p.createdAt)}
            </span>
          </div>
        )}
        <Body text={p.body} agents={agents} />
        <div className="flex flex-wrap items-center gap-3">
          {!inThread && p.replyCount > 0 && onThread ? (
            <button
              type="button"
              onClick={() => onThread(p.id)}
              className="mt-1 inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-accent hover:underline"
            >
              <IconChat size={12} />
              {t('talk.replies', { count: p.replyCount })}
              {p.lastReplyAt ? (
                <span className="font-normal text-faint">· {relTime(p.lastReplyAt)}</span>
              ) : null}
            </button>
          ) : null}
          {p.runId ? (
            <Link
              to="/runs/$id"
              params={{ id: p.runId }}
              className="mt-0.5 text-2xs text-faint hover:text-accent hover:underline"
            >
              {t('talk.fromRun')}
            </Link>
          ) : null}
        </div>
      </div>
      {!inThread && onThread ? (
        <button
          type="button"
          onClick={() => onThread(p.id)}
          aria-label={t('talk.replyInThread')}
          title={t('talk.replyInThread')}
          className="absolute top-1 right-3 hidden size-7 items-center justify-center rounded-md border border-border bg-panel text-muted shadow-soft group-hover:inline-flex hover:text-fg focus-visible:inline-flex"
        >
          <IconChat size={13} />
        </button>
      ) : null}
    </li>
  );
}

function Feed({
  posts,
  agents,
  onThread,
  inThread,
  empty,
  label,
}: {
  label: string;
  posts: ChannelPostDto[];
  agents: Agents;
  onThread?: (id: string) => void;
  inThread?: boolean;
  empty: string;
}) {
  const { i18n } = useTranslation();
  const ref = useRef<HTMLUListElement>(null);
  const last = posts.at(-1)?.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: follow the newest post
  useEffect(() => {
    ref.current?.scrollTo?.({ top: ref.current.scrollHeight });
  }, [last]);
  if (!posts.length) return <p className="flex-1 p-8 text-center text-sm text-muted">{empty}</p>;
  const grouped = withContinuation(posts);
  return (
    <ul ref={ref} aria-label={label} className="min-h-0 flex-1 overflow-y-auto py-2">
      {grouped.map(({ post, continued }, i) => {
        const day = newDay(grouped[i - 1]?.post.createdAt, post.createdAt);
        return (
          <Fragment key={post.id}>
            {day && !inThread ? (
              <li className="my-2 flex items-center gap-3 px-4 text-2xs font-medium text-faint" aria-hidden>
                <span className="h-px flex-1 bg-border" />
                {new Date(post.createdAt).toLocaleDateString(i18n.language, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
                <span className="h-px flex-1 bg-border" />
              </li>
            ) : null}
            <Message
              p={post}
              continued={continued && !day}
              agents={agents}
              {...(onThread ? { onThread } : {})}
              {...(inThread ? { inThread } : {})}
            />
          </Fragment>
        );
      })}
    </ul>
  );
}

/** Message box with @mention completion (agent slugs); Enter sends, Shift+Enter is a new line. */
function Composer({
  channel,
  replyToId,
  agents,
  placeholder,
}: {
  channel: ChannelDto;
  replyToId?: string;
  agents: Agents;
  placeholder: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [pick, setPick] = useState(0);
  const area = useRef<HTMLTextAreaElement>(null);
  const send = useApiMutation('createChannelPost', { invalidate: [qk.channels] });
  const mention = mentionAt(text, caret);
  const options = mention
    ? agents.list
        .filter((a) => a.slug.startsWith(mention.query) || a.name.toLowerCase().startsWith(mention.query))
        .slice(0, 6)
    : [];
  const complete = (a: AgentDto) => {
    if (!mention) return;
    const next = `${text.slice(0, mention.start)}@${a.slug} ${text.slice(caret)}`;
    const pos = mention.start + a.slug.length + 2;
    setText(next);
    setCaret(pos);
    requestAnimationFrame(() => area.current?.setSelectionRange(pos, pos));
  };
  const submit = () => {
    const body = text.trim();
    if (!body) return;
    send.mutate(
      { params: { id: channel.id }, body: { text: body, ...(replyToId ? { replyToId } : {}) } },
      { onSuccess: () => setText('') },
    );
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (options.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setPick((p) => (p + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        complete(options[Math.min(pick, options.length - 1)]!);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };
  return (
    <form
      className="relative shrink-0 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {options.length ? (
        <ul
          aria-label={t('talk.mention')}
          className="absolute right-3 bottom-full left-3 mb-1 overflow-hidden rounded-xl border border-border bg-panel shadow-pop"
        >
          {options.map((a, i) => (
            <li key={a.id}>
              <button
                type="button"
                aria-current={i === pick ? 'true' : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  complete(a);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                  i === pick && 'bg-hover',
                )}
              >
                <AgentOrb seed={a.id} size={20} avatar={a.avatar} name={a.name} />
                <span className="font-medium">{a.name}</span>
                <span className="text-xs text-muted">@{a.slug}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-end gap-2 rounded-xl border border-border bg-bg p-1.5 focus-within:border-accent">
        <Textarea
          ref={area}
          rows={1}
          value={text}
          maxLength={2000}
          aria-label={placeholder}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
            setPick(0);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
          onKeyDown={onKey}
          className="max-h-40 min-h-9 flex-1 resize-none border-0 bg-transparent focus:ring-0"
        />
        <Button type="submit" variant="primary" size="sm" loading={send.isPending} disabled={!text.trim()}>
          {t('channels.send')}
        </Button>
      </div>
    </form>
  );
}

function Typing({ channel, agents }: { channel: ChannelDto; agents: Agents }) {
  const { t } = useTranslation();
  const names = channel.activeAgentIds.map((id) => nameOf(agents, id)).filter(Boolean);
  return (
    <p aria-live="polite" className="h-5 shrink-0 px-4 text-xs text-muted">
      {names.length ? (
        <>
          <span className="mr-1.5 inline-block size-1.5 animate-pulse-dot rounded-full bg-claude align-middle" />
          {t('talk.isReplying', { count: names.length, names: names.join(', ') })}
        </>
      ) : null}
    </p>
  );
}

function ThreadPane({
  channel,
  thread,
  agents,
  onClose,
  canPost,
}: {
  channel: ChannelDto;
  thread: string;
  agents: Agents;
  onClose: () => void;
  canPost: boolean;
}) {
  const { t } = useTranslation();
  const posts = useChannelThread(channel.id, thread);
  return (
    <aside
      aria-label={t('talk.thread')}
      className="flex min-h-0 flex-col border-border bg-panel max-lg:absolute max-lg:inset-0 max-lg:z-10 lg:w-[380px] lg:shrink-0 lg:border-l"
    >
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
        <h3 className="text-sm font-semibold">{t('talk.thread')}</h3>
        <span className="truncate text-xs text-muted">
          {channel.kind === 'dm' ? channel.name : `#${channel.slug}`}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="ml-auto inline-flex size-8 items-center justify-center rounded-md text-muted hover:bg-hover hover:text-fg"
        >
          <IconX size={14} />
        </button>
      </header>
      {posts.isPending ? (
        <Spinner />
      ) : (
        <Feed
          posts={posts.data ?? []}
          agents={agents}
          inThread
          label={t('talk.thread')}
          empty={t('channels.noPosts')}
        />
      )}
      {canPost ? (
        <Composer
          channel={channel}
          replyToId={thread}
          agents={agents}
          placeholder={t('talk.replyPlaceholder')}
        />
      ) : null}
    </aside>
  );
}

/**
 * One channel or agent DM, Slack-style: header with members, the conversation (grouped, platform lines,
 * threads), who is replying right now, and a composer with @mentions. People can always read along.
 */
export function ChannelView({
  channelId,
  thread: initialThread,
  onThreadChange,
  compact,
}: {
  channelId: string;
  thread?: string | undefined;
  onThreadChange?: (thread: string | undefined) => void;
  /** Inside the Company page column: no member header, thread opens over the feed. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const channels = useChannels();
  const agents = useAgentMaps();
  const canPost = useCan('conversation:create');
  const canManage = useCan('agent:update');
  const [manage, setManage] = useState(false);
  const [thread, setThreadState] = useState(initialThread);
  useEffect(() => setThreadState(initialThread), [initialThread]);
  const setThread = (id: string | undefined) => {
    setThreadState(id);
    onThreadChange?.(id);
  };
  const channel = (channels.data ?? []).find((c) => c.id === channelId) ?? null;
  const posts = useChannelPosts(channel?.id ?? null);
  const markRead = useApiMutation('markChannelRead', { invalidate: [qk.channels] });
  const lastId = posts.data?.at(-1)?.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: mark read when opened and when new posts arrive
  useEffect(() => {
    if (channel && (channel.unread > 0 || lastId)) markRead.mutate({ params: { id: channel.id } });
  }, [channel?.id, lastId]);

  if (channels.error) return <ErrorBox error={channels.error} />;
  if (channels.isPending) return <Spinner />;
  if (!channel) return <p className="p-8 text-center text-sm text-muted">{t('talk.notFound')}</p>;
  const members = channel.members.map((m) => agents.byId.get(m.agentId)).filter(Boolean) as AgentDto[];
  const creator = channel.createdByAgentId ? agents.byId.get(channel.createdByAgentId) : undefined;
  const title = channel.kind === 'dm' ? channel.name : `#${channel.slug}`;

  return (
    <div className="relative flex min-h-0 flex-1">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {compact ? null : (
          <header className="flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-2">
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold">{title}</h2>
              <p className="truncate text-xs text-muted">
                {channel.kind === 'dm'
                  ? t('talk.dmHint')
                  : [channel.topic, creator ? t('talk.createdBy', { name: creator.name }) : null]
                      .filter(Boolean)
                      .join(' · ')}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="flex" title={members.map((m) => m.name).join(', ')}>
                {members.slice(0, 5).map((a, i) => (
                  <AgentOrb
                    key={a.id}
                    seed={a.id}
                    size={24}
                    avatar={a.avatar}
                    name={a.name}
                    className={cn(i > 0 && '-ml-1.5 ring-2 ring-panel')}
                  />
                ))}
                {members.length > 5 ? (
                  <span className="-ml-1.5 flex size-6 items-center justify-center rounded-full bg-panel-2 text-2xs ring-2 ring-panel">
                    +{members.length - 5}
                  </span>
                ) : null}
              </span>
              {canManage && channel.kind === 'channel' ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('channels.manage')}
                  onClick={() => setManage(true)}
                >
                  <IconSettings size={14} />
                </Button>
              ) : null}
            </div>
          </header>
        )}
        {isTemporaryChannel(channel) ? (
          <TemporaryBanner channel={channel} creator={creator?.name} canClose={canManage} />
        ) : null}
        {posts.isPending ? (
          <Spinner />
        ) : (
          <Feed
            posts={posts.data ?? []}
            agents={agents}
            onThread={setThread}
            label={channel.kind === 'dm' ? channel.name : `#${channel.slug}`}
            empty={channel.kind === 'dm' ? t('talk.dmEmpty') : t('channels.noPosts')}
          />
        )}
        <Typing channel={channel} agents={agents} />
        {canPost && !channel.archived ? (
          <Composer
            channel={channel}
            agents={agents}
            placeholder={
              channel.kind === 'dm'
                ? t('talk.dmPlaceholder', { name: channel.name })
                : t('channels.compose', { slug: channel.slug })
            }
          />
        ) : null}
      </div>
      {thread ? (
        <ThreadPane
          channel={channel}
          thread={thread}
          agents={agents}
          canPost={canPost}
          onClose={() => setThread(undefined)}
        />
      ) : null}
      {manage ? (
        <ManageChannelDialog channel={channel} agents={agents.list} onClose={() => setManage(false)} />
      ) : null}
    </div>
  );
}

/** Agents' coordination channel: open until a person closes it, so people can follow what was agreed. */
function TemporaryBanner({
  channel,
  creator,
  canClose,
}: {
  channel: ChannelDto;
  creator?: string;
  canClose: boolean;
}) {
  const { t } = useTranslation();
  const close = useApiMutation('updateChannel', { invalidate: [qk.channels] });
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-panel-2 px-4 py-2 text-xs text-muted">
      <span className="min-w-0 flex-1">
        {channel.archived
          ? t('talk.temporaryClosed')
          : t('talk.temporaryHint', { name: creator ?? t('talk.anAgent') })}
      </span>
      {canClose && !channel.archived ? (
        <Button
          size="sm"
          variant="outline"
          disabled={close.isPending}
          onClick={() => close.mutate({ params: { id: channel.id }, body: { archived: true } })}
        >
          {t('talk.closeChannel')}
        </Button>
      ) : null}
    </div>
  );
}
